/**
 * RecruitEmailComposer — Phase K.3.2 admin modal.
 *
 * Opens from K.3's "Recruit partners" red badge. Prefills subject +
 * body from the row context (ZIP + past-30-day booking count). Admin
 * can edit both fields freely. Send fires via Resend on the backend
 * and writes an audit row.
 *
 * Copy discipline: Family H bans + Family G-style rate-derivable
 * framing bans. All copy in this component (labels, placeholders,
 * toasts) audited by PK3.2.7.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Mail,
  X,
  Copy,
  Loader2,
  Send,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const _RECRUIT_LINK_DEFAULT = "https://poopluckers.com/partner/tiers";
const _EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function _buildDefaultSubject(zipCode, past) {
  return `${past} customers in ${zipCode} are looking for a PooPartner`;
}

function _buildDefaultBody(zipCode, past, firstName, recruitLink) {
  const name = (firstName && firstName.trim()) || "there";
  return [
    `Hi ${name},`,
    "",
    `Quick note — PooPluckers has ${past} booking requests from customers`,
    `in ${zipCode} over the last 30 days, and zero PooPartners serving that`,
    "ZIP right now.",
    "",
    "That's the kind of built-in demand that's hard to find anywhere else.",
    "",
    `If you or a crew you know is running dog waste pickup in ${zipCode}, we'd`,
    "love to put those customers directly in your queue. Onboarding takes",
    "about 20 minutes:",
    "",
    `  ${recruitLink}`,
    "",
    "Identity is confirmed through Stripe, payouts go straight to your bank,",
    "and we handle scheduling, customer support, and dispute flow so you can",
    "focus on the yards.",
    "",
    "Reply here if you want to chat first — happy to walk through it.",
    "",
    "— The PooPluckers team",
  ].join("\n");
}

/* ------------------------------------------------------------------ *
 * Modal
 * ------------------------------------------------------------------ */
export default function RecruitEmailComposer({
  open,
  onClose,
  zipCode,
  pastBookings,
  onSent,
}) {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientFirstName, setRecipientFirstName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [erroredMessage, setErroredMessage] = useState("");

  // Reset & prefill when the modal opens with a new context.
  useEffect(() => {
    if (open) {
      setRecipientEmail("");
      setRecipientFirstName("");
      setSubject(_buildDefaultSubject(zipCode, pastBookings));
      setBody(
        _buildDefaultBody(zipCode, pastBookings, "", _RECRUIT_LINK_DEFAULT),
      );
      setErroredMessage("");
    }
  }, [open, zipCode, pastBookings]);

  // Re-render the body's first-name greeting when the admin types a
  // first name — but ONLY if the body hasn't been manually edited past
  // the greeting (i.e., admin is still on the default). Cheap heuristic:
  // if body starts with "Hi there," (or "Hi <blank>,"), overwrite the
  // greeting line; otherwise leave alone.
  useEffect(() => {
    if (!open) return;
    setBody((prev) => {
      const lines = prev.split("\n");
      if (lines.length === 0) return prev;
      const firstLine = lines[0];
      if (/^Hi\s+[A-Za-z]*,?\s*$/.test(firstLine.trim())) {
        const name = (recipientFirstName && recipientFirstName.trim()) || "there";
        lines[0] = `Hi ${name},`;
        return lines.join("\n");
      }
      return prev;
    });
  }, [recipientFirstName, open]);

  const emailValid = useMemo(
    () => _EMAIL_RE.test(recipientEmail.trim()),
    [recipientEmail],
  );

  const handleSend = async () => {
    if (!emailValid || sending) return;
    setSending(true);
    setErroredMessage("");
    try {
      const res = await api.post("/admin/recruit-email/send", {
        recipient_email: recipientEmail.trim(),
        recipient_first_name: recipientFirstName.trim() || null,
        zip: String(zipCode),
        past_30d_bookings: Number(pastBookings),
        subject,
        body,
      });
      toast.success(
        `Recruit email sent to ${recipientEmail.trim()}.`,
      );
      if (onSent) onSent(res.data);
      onClose();
    } catch (e) {
      const msg =
        formatApiErrorDetail(e?.response?.data?.detail) ||
        e?.response?.data?.detail?.message ||
        e?.message ||
        "Send failed. Try again.";
      setErroredMessage(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${body}`);
      toast.success("Subject + body copied to clipboard.");
    } catch {
      toast.error("Couldn't access the clipboard. Try again.");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="recruit-email-composer-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recruit-email-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17, 24, 39, 0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "40px 16px",
        zIndex: 1000,
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div
        data-testid="recruit-email-composer-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#ffffff",
          borderRadius: 14,
          maxWidth: 640,
          width: "100%",
          boxShadow: "0 25px 45px -12px rgba(0,0,0,0.25)",
          padding: 24,
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Mail size={20} color="#F89623" />
            <h2
              id="recruit-email-title"
              data-testid="recruit-email-title"
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 800,
                color: "#374751",
              }}
            >
              Recruit a PooPartner in {zipCode}
            </h2>
          </div>
          <button
            data-testid="recruit-email-close"
            aria-label="Close"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 4,
            }}
          >
            <X size={20} color="#6b7280" />
          </button>
        </header>

        <p
          data-testid="recruit-email-context"
          style={{ margin: "0 0 16px 0", fontSize: 13, color: "#6b7280" }}
        >
          <b>{pastBookings}</b> booking requests came from {zipCode} in the
          last 30 days with <b>0</b> PooPartners serving that ZIP.
        </p>

        <label style={_labelStyle}>Recipient email</label>
        <input
          data-testid="recruit-email-recipient"
          type="email"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          placeholder="crew@example.com"
          style={_inputStyle(!emailValid && recipientEmail.length > 0)}
        />
        {recipientEmail.length > 0 && !emailValid && (
          <div style={_hintErrorStyle} data-testid="recruit-email-invalid-hint">
            That doesn't look like a valid email address.
          </div>
        )}

        <label style={_labelStyle}>Recipient first name (optional)</label>
        <input
          data-testid="recruit-email-first-name"
          type="text"
          value={recipientFirstName}
          onChange={(e) => setRecipientFirstName(e.target.value)}
          placeholder="Alex"
          style={_inputStyle(false)}
        />

        <label style={_labelStyle}>Subject</label>
        <input
          data-testid="recruit-email-subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          style={_inputStyle(false)}
        />

        <label style={_labelStyle}>Body</label>
        <textarea
          data-testid="recruit-email-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={12}
          style={{
            ..._inputStyle(false),
            fontFamily:
              "SF Mono, Menlo, Consolas, 'Liberation Mono', monospace",
            fontSize: 12,
            lineHeight: 1.5,
            resize: "vertical",
            minHeight: 200,
          }}
        />

        {erroredMessage && (
          <div
            data-testid="recruit-email-error"
            style={{
              marginTop: 10,
              padding: "10px 12px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              color: "#b91c1c",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <AlertTriangle size={16} />
            <span>{erroredMessage}</span>
          </div>
        )}

        <div
          style={{
            marginTop: 18,
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="ghost"
            data-testid="recruit-email-copy"
            onClick={handleCopy}
            disabled={sending}
          >
            <Copy size={14} style={{ marginRight: 6 }} />
            Copy to clipboard
          </Button>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="ghost"
              data-testid="recruit-email-cancel"
              onClick={onClose}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              data-testid="recruit-email-send"
              onClick={handleSend}
              disabled={!emailValid || sending}
              style={{ background: "#F89623", color: "#ffffff" }}
            >
              {sending ? (
                <>
                  <Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />
                  Sending…
                </>
              ) : (
                <>
                  <Send size={14} style={{ marginRight: 6 }} />
                  Send via Resend
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Sent-count badge (rendered inline in the metro-coverage row)
 * ------------------------------------------------------------------ */
export function RecruitSentCountBadge({ count }) {
  if (!count || count < 1) return null;
  return (
    <span
      data-testid="recruit-sent-badge"
      title={`${count} recruit email${count === 1 ? "" : "s"} sent for this ZIP`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: "#ecfdf5",
        color: "#065f46",
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 700,
        marginLeft: 6,
        border: "1px solid #a7f3d0",
      }}
    >
      <CheckCircle2 size={11} />
      {count} sent
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Shared inline styles
 * ------------------------------------------------------------------ */
const _labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "#6b7280",
  marginTop: 12,
  marginBottom: 4,
};

const _inputStyle = (errored) => ({
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 10px",
  border: `1px solid ${errored ? "#fca5a5" : "#e5e7eb"}`,
  borderRadius: 6,
  fontSize: 13,
  color: "#374751",
  outline: "none",
  background: errored ? "#fef2f2" : "#ffffff",
});

const _hintErrorStyle = {
  fontSize: 12,
  color: "#b91c1c",
  marginTop: 4,
};
